import React, { useState, useEffect, useRef, useCallback } from 'react';
import ResultCard from './search/ResultCard';
import ThumbnailHoverModal from './ThumbnailHoverModal';
import PDPModal from './PDPModal';
import SearchService from '../services/SearchService';
import { preloadSlimEventDataForChannels } from '../utils/channelDateUtils';

/**
 * FighterTitlesGrid
 *
 * Shows the titles associated with a fighter as a grid of cards, using the exact
 * same card + hover-preview experience as the /search page. Titles are fetched
 * through the same SearchService path the search page uses (channel search by the
 * fighter's name), and cards reuse ResultCard + ThumbnailHoverModal + PDPModal so
 * hover/click behaviour is identical.
 */
const FighterTitlesGrid = ({ fighterName }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setCmsHydrationTick] = useState(0);

  // Hover modal state (mirrors SearchResults)
  const [hoverModalData, setHoverModalData] = useState({
    isVisible: false,
    channel: null,
    position: { x: 0, y: 0 },
  });
  const [hoveredCardIndex, setHoveredCardIndex] = useState(-1);

  // PDP modal state
  const [pdpModalData, setPdpModalData] = useState({
    isVisible: false,
    channel: null,
    videoState: { autoPlay: false, currentTime: 0 },
  });

  const hoverTimeoutRef = useRef(null);

  // Fetch this fighter's titles via the same search path the /search page uses
  useEffect(() => {
    let active = true;
    if (fighterName) {
      setLoading(true);
      SearchService.searchChannels(fighterName, 'website')
        .then(data => {
          if (active) {
            setResults(Array.isArray(data?.channels) ? data.channels : []);
          }
        })
        .catch(err => {
          console.error('Error fetching fighter titles:', err);
          if (active) {
            setResults([]);
          }
        })
        .finally(() => {
          if (active) {
            setLoading(false);
          }
        });
    }
    return () => {
      active = false;
    };
  }, [fighterName]);

  // Preload slim event data so cards can show their event-date labels
  useEffect(() => {
    let isMounted = true;
    const preload = async () => {
      if (!results?.length) {
        return;
      }
      const preloadedCount = await preloadSlimEventDataForChannels(results);
      if (isMounted && preloadedCount > 0) {
        setCmsHydrationTick(v => v + 1);
      }
    };
    preload();
    return () => {
      isMounted = false;
    };
  }, [results]);

  const showNewModal = useCallback((channel, position, index) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverModalData({ isVisible: true, channel, position });
      setHoveredCardIndex(index);
    }, 500);
  }, []);

  const handleHoverModalShow = useCallback(
    (channel, position, index) => {
      if (hoverModalData.isVisible && hoveredCardIndex !== index) {
        setHoverModalData(prev => ({ ...prev, isVisible: false }));
        setTimeout(() => showNewModal(channel, position, index), 250);
      } else {
        showNewModal(channel, position, index);
      }
    },
    [hoverModalData.isVisible, hoveredCardIndex, showNewModal],
  );

  const handleHoverModalHide = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverModalData(prev => ({ ...prev, isVisible: false }));
      setHoveredCardIndex(-1);
    }, 300);
  }, []);

  const handleModalMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handleModalMouseLeave = useCallback(() => {
    handleHoverModalHide();
  }, [handleHoverModalHide]);

  const handleOpenPDPModal = useCallback(
    videoState => {
      setHoverModalData(prev => ({ ...prev, isVisible: false }));
      setPdpModalData({
        isVisible: true,
        channel: hoverModalData.channel,
        videoState: videoState || { autoPlay: false, currentTime: 0 },
      });
    },
    [hoverModalData.channel],
  );

  const handleOpenPDPModalDirect = useCallback((channel, videoState) => {
    setHoverModalData(prev => ({ ...prev, isVisible: false }));
    setPdpModalData({
      isVisible: true,
      channel,
      videoState: videoState || { autoPlay: false, currentTime: 0 },
    });
  }, []);

  const handleClosePDPModal = useCallback(() => {
    setPdpModalData({
      isVisible: false,
      channel: null,
      videoState: { autoPlay: false, currentTime: 0 },
    });
  }, []);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="fighter-titles">
      <h2 className="fighter-titles__heading">Fights &amp; Titles</h2>

      {loading ? (
        <div className="fighter-content__loading">
          <div className="fighter-content__spinner" />
        </div>
      ) : results.length === 0 ? (
        <div className="fighter-titles__empty">No titles found for this fighter.</div>
      ) : (
        <div className="relative">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {results.map((result, index) => (
              <div key={result._id || index} className="channel-card-container">
                <ResultCard
                  result={result}
                  index={index}
                  onHoverModalShow={handleHoverModalShow}
                  onHoverModalHide={handleHoverModalHide}
                  onOpenPDPModal={handleOpenPDPModalDirect}
                />
              </div>
            ))}
          </div>

          <ThumbnailHoverModal
            channel={hoverModalData.channel}
            isVisible={hoverModalData.isVisible}
            position={hoverModalData.position}
            onMouseEnter={handleModalMouseEnter}
            onMouseLeave={handleModalMouseLeave}
            onOpenPDPModal={handleOpenPDPModal}
          />
        </div>
      )}

      <PDPModal
        isVisible={pdpModalData.isVisible}
        channel={pdpModalData.channel}
        onClose={handleClosePDPModal}
        videoState={pdpModalData.videoState}
      />
    </div>
  );
};

export default FighterTitlesGrid;
